import { useEffect } from 'react';
import { useCurrencyRate } from '../../hooks/useCurrencyRate';
import { Champ, SaisieNombre, Selection } from './ui/Champ';

/**
 * Dépense payée dans une autre devise (R-CTL-07, D3.7).
 *
 * Ce que Gertrude convertit n'est qu'une PROPOSITION : le taux du jour n'est
 * jamais exactement celui que la banque a appliqué, entre sa propre marge et la
 * date de valeur. Le montant en euros reste donc modifiable, et c'est le
 * relevé bancaire qui fait foi. Au-delà de l'écart toléré, la dépense part
 * signalée au valideur, qui peut réclamer la preuve bancaire.
 *
 * Le taux vient du convertisseur déjà utilisé par les factures fournisseurs —
 * même source, même cache, aucune deuxième implémentation à maintenir.
 */

/** Devises que l'on rencontre réellement. La liste reste ouverte à la saisie. */
const DEVISES = ['CHF', 'GBP', 'USD', 'CAD', 'SEK', 'NOK', 'DKK', 'PLN', 'CZK', 'MAD', 'TND'];

export function ConversionDevise({
  devise,
  montantOriginal,
  taux,
  onChange,
}: {
  devise: string | null;
  montantOriginal: number | null;
  taux: number | null;
  /** Renvoie aussi le montant en euros : c'est lui qui alimente le formulaire. */
  onChange: (v: {
    devise: string | null;
    montant_original: number | null;
    taux_change: number | null;
    montant_eur: number | null;
  }) => void;
}) {
  const { data: tauxDuJour, isLoading, isError } = useCurrencyRate(devise);

  // Le taux du jour ne s'impose qu'à défaut de taux déjà retenu : écraser une
  // valeur corrigée à la main à chaque rafraîchissement serait insupportable.
  const tauxApplique = taux ?? tauxDuJour?.rate ?? null;
  const montantEur =
    montantOriginal !== null && tauxApplique !== null
      ? Math.round(montantOriginal * tauxApplique * 100) / 100
      : null;

  // Le taux du jour arrive APRÈS l'affichage. Sans cela, le montant en euros
  // resterait à zéro dans le formulaire alors qu'il est affiché juste à côté —
  // et le salarié devrait le recopier à la main.
  useEffect(() => {
    if (taux === null && tauxDuJour?.rate && montantOriginal !== null) {
      onChange({
        devise,
        montant_original: montantOriginal,
        taux_change: tauxDuJour.rate,
        montant_eur: Math.round(montantOriginal * tauxDuJour.rate * 100) / 100,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tauxDuJour?.rate, montantOriginal, taux]);

  function majuscule(v: string) {
    return v.trim().toUpperCase() || null;
  }

  function recalculer(next: {
    devise?: string | null;
    montant_original?: number | null;
    taux_change?: number | null;
  }) {
    const d = next.devise !== undefined ? next.devise : devise;
    const m = next.montant_original !== undefined ? next.montant_original : montantOriginal;
    // Changer de devise invalide le taux : celui de la précédente n'a plus rien
    // à voir avec la nouvelle.
    const t =
      next.taux_change !== undefined
        ? next.taux_change
        : next.devise !== undefined && next.devise !== devise
          ? null
          : tauxApplique;
    onChange({
      devise: d,
      montant_original: m,
      taux_change: t,
      montant_eur: m !== null && t !== null ? Math.round(m * t * 100) / 100 : null,
    });
  }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-4">
        <Champ label="Devise">
          <Selection
            value={devise ?? ''}
            onChange={(e) => recalculer({ devise: majuscule(e.target.value) })}
          >
            <option value="">Choisir…</option>
            {DEVISES.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </Selection>
        </Champ>
        <Champ label={`Montant payé${devise ? ` en ${devise}` : ''}`}>
          <SaisieNombre
            nullable
            valeur={montantOriginal}
            onValeur={(v) => recalculer({ montant_original: v })}
          />
        </Champ>
      </div>

      {devise && (
        <div className="grid grid-cols-2 gap-4">
          <Champ
            label="Taux appliqué"
            aide={
              isLoading
                ? 'Recherche du taux du jour…'
                : isError
                  ? 'Taux du jour indisponible : saisissez-le.'
                  : tauxDuJour?.source === 'fallback'
                    ? 'Taux de secours : vérifiez sur votre relevé.'
                    : undefined
            }
          >
            <SaisieNombre
              nullable
              valeur={tauxApplique}
              onValeur={(v) => recalculer({ taux_change: v })}
            />
          </Champ>
          {/* Champ calculé : fond distinct, non saisissable ici. Il se corrige
              dans « Prix unitaire HT », qui est le montant retenu. */}
          <Champ label="Équivalent en euros">
            <div className="flex min-h-11 items-center rounded-md bg-ciel/25 px-3 text-sm font-semibold text-nuit">
              {montantEur !== null
                ? montantEur.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })
                : '-'}
            </div>
          </Champ>
        </div>
      )}

      <p className="text-xs text-nuit/60">
        Le montant en euros ci-dessus n’est qu’une conversion au taux du jour. Si votre relevé
        bancaire porte une autre somme, corrigez le prix unitaire : c’est lui qui sera remboursé.
      </p>
    </div>
  );
}
